import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { formatTimestampShort } from '@chainflip/utils/date';
import { abbreviate } from '@chainflip/utils/string';
import { tickToRate } from '@chainflip/utils/tickMath';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { getPercentageFilled, LimitOrderStatus } from '@/shared/components/orders/OrderStatus';
import {
  BuyPill,
  OrderPrice,
  OrderTypePill,
  SellPill,
} from '@/shared/components/orders/RangeOrderRow';
import { chainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

type LimitOrderRowProps = {
  baseAsset: BaseChainflipAsset;
  lpAccount: string | null;
  creationTimestamp?: string;
  displayCreationTimestamp?: boolean;
  onClick?: (asset: BaseChainflipAsset) => void;
  onCloseLimitOrder?: (orderId: string) => void;
  orderId?: string;
  isMobileView?: boolean;
  remainingSellAmount: bigint;
  originalSellAmount: bigint;
  tick: number;
  limitType: 'Sell' | 'Buy';
  isOrderOpen: boolean;
  additionalCells?: React.ReactNode;
};

type LimitOrderRowMobileProps = Omit<
  LimitOrderRowProps,
  'limitType' | 'lpAccount' | 'onClick' | 'isMobileView' | 'additionalCells'
> & {
  isSell: boolean;
  originalSellTokenAmount: TokenAmount;
  buyTokenAmount: TokenAmount;
  baseAssetSymbol: string;
};

type LimitOrderRowDesktopProps = LimitOrderRowMobileProps &
  Pick<LimitOrderRowProps, 'onClick' | 'lpAccount' | 'additionalCells'>;

const LimitOrderRowMobile = ({
  isSell,
  remainingSellAmount,
  originalSellAmount,
  isOrderOpen,
  baseAsset,
  originalSellTokenAmount,
  buyTokenAmount,
  baseAssetSymbol,
  tick,
  displayCreationTimestamp = true,
  creationTimestamp,
}: LimitOrderRowMobileProps) => {
  const usdcSymbol = chainflipAssetMap.Usdc.symbol;

  return (
    <tr className="flex flex-col justify-center gap-y-2 p-3 text-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <OrderTypePill type="LIMIT" />
          {isSell ? <SellPill /> : <BuyPill />}
        </div>
        <LimitOrderStatus
          fillPercentage={getPercentageFilled(remainingSellAmount, originalSellAmount)}
          isOrderOpen={isOrderOpen}
        />
      </div>
      <div className="flex justify-between gap-x-1 align-top">
        <div className="flex flex-col gap-y-0.5">
          <div className="flex items-center gap-x-2">
            <div className="flex flex-col gap-y-2">
              <span className="flex gap-x-2 text-white">
                <TokenWithChainByAsset asset={baseAsset} />
                <span>
                  {isSell ? originalSellTokenAmount?.toFormatted() : buyTokenAmount?.toFormatted()}
                </span>
                <span>{baseAssetSymbol}</span>
              </span>
            </div>
          </div>
          <span>
            for <OrderPrice baseAsset={baseAsset} tick={tick} /> {usdcSymbol}
          </span>
        </div>
      </div>
      {displayCreationTimestamp && (
        <span>
          {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
        </span>
      )}
    </tr>
  );
};

const LimitOrderRowDesktop = ({
  onClick,
  baseAsset,
  isSell,
  originalSellTokenAmount,
  buyTokenAmount,
  baseAssetSymbol,
  tick,
  lpAccount,
  remainingSellAmount,
  originalSellAmount,
  isOrderOpen,
  displayCreationTimestamp = true,
  creationTimestamp,
  additionalCells,
}: LimitOrderRowDesktopProps) => (
  <ClickableTableRow
    disableHoverStyle={!onClick}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(baseAsset);
    }}
  >
    <td className="whitespace-nowrap text-12">
      <div className="flex gap-x-2 whitespace-nowrap">
        <OrderTypePill type="LIMIT" />
        {isSell ? <SellPill /> : <BuyPill />}
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex items-center gap-x-2 text-cf-light-3">
        <div className="flex flex-col gap-y-2">
          <span className="flex items-center gap-x-2">
            <TokenWithChainByAsset asset={baseAsset} size="medium" />
            <span>
              {isSell ? originalSellTokenAmount?.toFormatted() : buyTokenAmount?.toFormatted()}
            </span>
            <span>{baseAssetSymbol}</span>
            <span className="text-cf-light-2">on {chainflipAssetMap[baseAsset].chain.name}</span>
          </span>
        </div>
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <OrderPrice baseAsset={baseAsset} tick={tick} />
    </td>
    {lpAccount && (
      <td className="whitespace-nowrap text-12">
        <Link href={`/lps/${lpAccount}`} noPropagate underline>
          <span>{abbreviate(lpAccount)}</span>
        </Link>
      </td>
    )}
    <td className="whitespace-nowrap text-12">
      <LimitOrderStatus
        fillPercentage={getPercentageFilled(remainingSellAmount, originalSellAmount)}
        isOrderOpen={isOrderOpen}
      />
    </td>
    {displayCreationTimestamp && (
      <td className="whitespace-nowrap text-12">
        {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
      </td>
    )}
    {additionalCells}
  </ClickableTableRow>
);

export const LimitOrderRow = (props: LimitOrderRowProps) => {
  const isSell = props.limitType === 'Sell';
  const rate = tickToRate(props.tick, props.baseAsset);
  const sellAsset = isSell ? props.baseAsset : 'Usdc';

  const originalSellTokenAmount = TokenAmount.fromAsset(props.originalSellAmount, sellAsset);
  const buyTokenAmount = originalSellTokenAmount.mul(isSell ? rate : 1 / rate);

  const baseAssetSymbol = chainflipAssetMap[props.baseAsset].symbol;

  return props.isMobileView ? (
    <LimitOrderRowMobile
      {...props}
      isSell={isSell}
      originalSellTokenAmount={originalSellTokenAmount}
      buyTokenAmount={buyTokenAmount}
      baseAssetSymbol={baseAssetSymbol}
    />
  ) : (
    <LimitOrderRowDesktop
      {...props}
      isSell={isSell}
      originalSellTokenAmount={originalSellTokenAmount}
      buyTokenAmount={buyTokenAmount}
      baseAssetSymbol={baseAssetSymbol}
    />
  );
};
